// 207B §5 [A DEFECT — PROD HAS IT TOO] — WHAT IS TYPED IS WHAT IS KEPT: ONE
// SAVE IN FLIGHT, THE LATEST VALUE WAITING.
//
// Laurent, typing into Settings through the tunnel: "the fields behave
// strangely, like there is autocorrect swallowing characters" — « Louizl »,
// « busl ». MEASURED on 9c323985 (scripts/drive-207b.js --part=typing, every
// PUT's answer held back 400 ms, a key every 110 ms, Chromium and WebKit):
// « Louizalaan 54 bus 3 » typed into a firm field and into a general-conditions
// box was shown AND stored « Loul b3 »; « 98765 » typed into a collective-case
// member's Quantum cell, 95. Every keystroke saves at once, so the answer to an
// earlier keystroke came back OVER the typing (and the keys typed meanwhile were
// lost), and two saves in flight could finish in either order.
//
// The rule, in one place per save path (src/App.tsx `saveFirmSettings` — every
// field of the Settings panes — and `persist`'s update branch — every row of
// every list): saves leave IN THE ORDER TYPED, one in flight, and the value
// typed meanwhile waits and leaves as soon as the one before it is back — the
// first key leaves at once (no new delay), and the queue outlives the pane that
// typed into it (nothing lost on closing it). What an answer may replace is the
// caller's rule: the waiting and in-flight values are handed back so it can lay
// them over the answer (`inflight()` / `waiting()`), or `mergeAnswer`
// (src/lib/savedRows.ts) keeps a row's fields changed after its request left.
// When an answer arrives the value waiting is put on the wire BEFORE anyone
// awaiting that answer resumes — so at that moment `inflight()` + `waiting()`
// is exactly what was typed after the answered request left.
//
// Pure: no React, no network — `send` is handed in. A recording drives it
// (recordings/207b-typed-kept). JSDoc-typed `.mjs`, as src/lib/firmAddress.mjs:
// the house eslint reads a parameter named inside a TypeScript function type as
// an unused variable (183's lesson), and a new file may carry no warning.

/**
 * @template T, R
 * @typedef {object} SaveQueue
 * @property {(value: T) => Promise<R>} run  queue a value; settles with the answer to the send that carried it (a later value may carry it: the latest wins)
 * @property {() => (T | null)} inflight  the value on the wire, or null
 * @property {() => (T | null)} waiting  the value typed since the one on the wire left, or null
 */

/**
 * @template T, R
 * @param {(value: T) => Promise<R>} send  sends one value, answers with the server's
 * @param {(waiting: T | null, next: T) => T} fold  what the waiting value becomes when another is queued behind it — `(w, next) => ({ ...w, ...next })` for patches, `(_, next) => next` for whole rows
 * @param {() => void} [onIdle]  called once nothing is on the wire and nothing waits
 * @returns {SaveQueue<T, R>}
 */
export function createSaveQueue(send, fold, onIdle) {
  /** @type {T | null} */
  let wire = null;
  /** @type {T | null} */
  let waiting = null;
  /** @type {{ resolve: (r: R) => void, reject: (e: unknown) => void }[]} */
  let waiters = [];
  /** @param {T} value @param {{ resolve: (r: R) => void, reject: (e: unknown) => void }[]} ws */
  const go = (value, ws) => {
    wire = value;
    const done = () => {
      wire = null;
      if (waiting !== null) {
        const v = waiting;
        const next = waiters;
        waiting = null;
        waiters = [];
        go(v, next);
      } else if (onIdle) onIdle();
    };
    send(value).then(
      (r) => {
        done();
        for (const w of ws) w.resolve(r);
      },
      (e) => {
        done();
        for (const w of ws) w.reject(e);
      },
    );
  };
  return {
    run(value) {
      return new Promise((resolve, reject) => {
        if (wire === null) go(value, [{ resolve, reject }]);
        else {
          waiting = fold(waiting, value);
          waiters.push({ resolve, reject });
        }
      });
    },
    inflight: () => wire,
    waiting: () => waiting,
  };
}
